/**
 * AdminLeadsExportTab - Export leads/contacts with advanced filters.
 *
 * Allows filtering by plan, subscription status, onboarding answers,
 * acquisition channel, funnel events, and date range.
 * Supports preview (JSON) and CSV download.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, Search, X, Loader2 } from 'lucide-react';
import {
  getPlans,
  getLeadsPreview,
  downloadLeadsCsv,
  type PlanData,
  type LeadExportFilters,
  type LeadPreviewData,
} from '@/services/adminService';

const FATURAMENTO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'nao_faturo', label: 'Não faturo' },
  { value: 'ate_10k', label: 'Até R$10k' },
  { value: '10k_50k', label: 'R$10k - R$50k' },
  { value: '50k_200k', label: 'R$50k - R$200k' },
  { value: 'acima_200k', label: 'Acima de R$200k' },
];

const OBJETIVO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'crescer_audiencia', label: 'Crescer audiência' },
  { value: 'gerar_leads', label: 'Gerar leads' },
  { value: 'vender_produtos', label: 'Vender produtos' },
  { value: 'posicionar_autoridade', label: 'Posicionar autoridade' },
  { value: 'testando', label: 'Testando' },
];

const TIPO_CONTEUDO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'reels_e_carrosseis', label: 'Reels e carrosséis' },
  { value: 'reels_raramente_carrosseis', label: 'Reels, raramente carrosséis' },
  { value: 'apenas_carrosseis', label: 'Apenas carrosséis' },
  { value: 'nao_produz', label: 'Não produz conteúdo' },
];

const LEADS_INSTAGRAM_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'sim_frequentemente', label: 'Sim, frequentemente' },
  { value: 'as_vezes', label: 'Às vezes' },
  { value: 'raramente', label: 'Raramente' },
];

const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'ACTIVE', label: 'Ativa' },
  { value: 'TRIALING', label: 'Trial' },
  { value: 'CANCELED', label: 'Cancelada' },
  { value: 'PAST_DUE', label: 'Atrasada' },
  { value: 'PENDING', label: 'Pendente' },
];

const BOOLEAN_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
];

const AdminLeadsExportTab: React.FC = () => {
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [filters, setFilters] = useState<LeadExportFilters>({});
  const [leads, setLeads] = useState<LeadPreviewData[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getPlans().then(({ plans }) => setPlans(plans)).catch(() => {});
  }, []);

  const updateFilter = (key: keyof LeadExportFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({});
    setLeads([]);
    setTotal(null);
  };

  const handlePreview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLeadsPreview(filters);
      setLeads(res.leads || []);
      setTotal(res.total ?? res.leads?.length ?? 0);
    } catch (err) {
      console.error('Failed to fetch leads preview', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleExport = () => {
    setExporting(true);
    try {
      downloadLeadsCsv(filters);
    } finally {
      setTimeout(() => setExporting(false), 1000);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const faturamentoLabel = (val: string) =>
    FATURAMENTO_OPTIONS.find((o) => o.value === val)?.label || val;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="border border-border rounded-lg p-4 space-y-4">
        <h3 className="text-sm font-semibold text-foreground">Filtros</h3>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {/* Plan */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Plano</label>
            <select
              value={filters.plan || ''}
              onChange={(e) => updateFilter('plan', e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground"
            >
              <option value="">Todos</option>
              {plans.map((p) => (
                <option key={p.id} value={p.slug}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Subscription Status */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Status Assinatura</label>
            <select
              value={filters.subscriptionStatus || ''}
              onChange={(e) => updateFilter('subscriptionStatus', e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground"
            >
              {SUBSCRIPTION_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Faturamento */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Faturamento</label>
            <select
              value={filters.faturamento || ''}
              onChange={(e) => updateFilter('faturamento', e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground"
            >
              {FATURAMENTO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Objetivo */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Objetivo</label>
            <select
              value={filters.objetivoCarrosseis || ''}
              onChange={(e) => updateFilter('objetivoCarrosseis', e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground"
            >
              {OBJETIVO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Tipo Conteúdo */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Tipo de Conteúdo</label>
            <select
              value={filters.tipoConteudo || ''}
              onChange={(e) => updateFilter('tipoConteudo', e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground"
            >
              {TIPO_CONTEUDO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Leads Instagram */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Leads no Instagram</label>
            <select
              value={filters.leadsInstagram || ''}
              onChange={(e) => updateFilter('leadsInstagram', e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground"
            >
              {LEADS_INSTAGRAM_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Channel */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Canal de Aquisição</label>
            <Input
              value={filters.channel || ''}
              onChange={(e) => updateFilter('channel', e.target.value)}
              placeholder="ex: meta_ads, direct"
              className="text-sm h-8"
            />
          </div>

          {/* Onboarding Completed */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Onboarding Completo</label>
            <select
              value={filters.onboardingCompleted || ''}
              onChange={(e) => updateFilter('onboardingCompleted', e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground"
            >
              {BOOLEAN_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Has Carousel */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Criou Carrossel</label>
            <select
              value={filters.hasCarousel || ''}
              onChange={(e) => updateFilter('hasCarousel', e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground"
            >
              {BOOLEAN_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Has Paid */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Fez Pagamento</label>
            <select
              value={filters.hasPaid || ''}
              onChange={(e) => updateFilter('hasPaid', e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground"
            >
              {BOOLEAN_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Criado a partir de</label>
            <Input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => updateFilter('dateFrom', e.target.value)}
              className="text-sm h-8"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Criado até</label>
            <Input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => updateFilter('dateTo', e.target.value)}
              className="text-sm h-8"
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handlePreview} disabled={loading} size="sm">
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
            Aplicar Filtros
          </Button>
          <Button onClick={handleExport} disabled={exporting} variant="outline" size="sm">
            {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Exportar CSV
          </Button>
          <Button onClick={clearFilters} variant="ghost" size="sm">
            <X className="w-4 h-4 mr-1.5" />
            Limpar Filtros
          </Button>
          {total !== null && (
            <span className="text-sm font-medium text-muted-foreground ml-auto">
              {total.toLocaleString('pt-BR')} lead{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Preview Table */}
      {leads.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Email</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Nome</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Handle</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Telefone</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Plano</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Faturamento</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Canal</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Carrossel</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Pagou</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Criado em</th>
                </tr>
              </thead>
              <tbody>
                {leads.slice(0, 50).map((lead, i) => (
                  <tr key={i} className="border-t border-border hover:bg-muted/30 transition-colors">
                    <td className="px-3 py-2 text-foreground">{lead.email}</td>
                    <td className="px-3 py-2 text-foreground">{lead.display_name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{lead.handle ? `@${lead.handle}` : ''}</td>
                    <td className="px-3 py-2 text-muted-foreground">{lead.phone}</td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                        {lead.plan || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{faturamentoLabel(lead.faturamento)}</td>
                    <td className="px-3 py-2 text-muted-foreground">{lead.first_touch_channel}</td>
                    <td className="px-3 py-2 text-center">{lead.created_carousel === 'Sim' ? '✓' : '—'}</td>
                    <td className="px-3 py-2 text-center">{lead.has_paid === 'Sim' ? '✓' : '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{formatDate(lead.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total !== null && total > 50 && (
            <div className="px-3 py-2 border-t border-border text-xs text-muted-foreground bg-muted/30">
              Mostrando 50 de {total.toLocaleString('pt-BR')} resultados. Exporte o CSV para ver todos.
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {total === 0 && !loading && (
        <div className="text-center py-12 text-muted-foreground text-sm">
          Nenhum lead encontrado com os filtros selecionados.
        </div>
      )}
    </div>
  );
};

export default AdminLeadsExportTab;
